import { Injectable } from '@angular/core';
import { UntypedFormGroup, UntypedFormBuilder, AbstractControl } from '@angular/forms';
import { Observable, Subject, BehaviorSubject, of } from 'rxjs';
import $ from 'jquery';

export const enum FORM_NAMES {
    addtl_costs = 'addtl-costs',
    addtl_costs_details = 'addtl-costs-details',
    calculation_details = 'calc-details',
    contract_shift = 'contract-info',
    facility_billing_details = 'facility-billing-details',
    housing = 'housing',
    housing_details = 'housing-details',
    insurance = 'insurance',
    insurance_details = 'insurance-details',
    overview = 'overview',
    pay_billing = 'pay-billing',
    reimbursements = 'reimbursements',
    reimbursement_details = 'reimbursement-details',
    stipend_details = 'stipend-details',
    vacation_details = 'vacation-details',
    pay_package = 'pay-package',
    tax_rate = 'tax-rate',
    pending_approval = 'pending-approval',
    margin_notes = 'margin-notes',
    pay_to = 'pay-to',
    for_referring = 'for-referring',
    margins_differences_details = 'margins-differences-details'
}

export const enum FORM_CONTROL_NAMES {
    // overview
    candidateName = 'candidateName',
    pendingFinancialOffer = 'pendingFinancialOffer',
    // pay billing
    basePayRate = 'basePayRate',
    otPayRate = 'otPayRate',
    baseBillRate = 'baseBillRate',
    holidayPayRate = 'holidayPayRate',
    holidayBillRate = 'holidayBillRate',
    onCallPayRate = 'onCallPayRate',
    onCallBillRate = 'onCallBillRate',
    callbackPayRate = 'callbackPayRate',
    callbackBillRate = 'callbackBillRate',
    chargePayRate = 'chargePayRate',
    chargeBillRate = 'chargeBillRate',
    billOvertime = 'billOvertime',
    overTimeRules = 'overTimeRules',
    overTimeHours = 'overTimeHours',
    otWeekOrDay = 'otWeekOrDay',
    andOr = 'andOr',
    contractFees = 'contractFees',
    taxable = 'taxable',
    orientation = 'orientation',
    orientHours = 'orientHours',
    orientRate = 'orientRate',
    orientType = 'orientType',
    orientHoursAlt = 'orientHoursAlt',
    orientRateAlt = 'orientRateAlt',
    orientTypeAlt = 'orientTypeAlt',
    weeklyStipends = 'weeklyStipends',
    paymentDiscount = 'paymentDiscount',
    // stipend details
    shiftHousing = 'shiftHousing',
    weeklyHousing = 'weeklyHousing',
    shiftMeals = 'shiftMeals',
    weeklyMeals = 'weeklyMeals',
    // addtl_costs_details
    ayaBooster = 'ayaBooster',
    internalCosts = 'internalCosts',
    // reimbursements
    relocationType = 'relocationType',
    relocationAmountTwo = 'relocationAmountTwo',
    relocationAmountOne = 'relocationAmountOne',
    oneTimeReimbursements = 'oneTimeReimbursements',
    oneTimeCarRental = 'oneTimeCarRental',
    oneTimeFingerprinting = 'oneTimeFingerprinting',
    oneTimeParking = 'oneTimeParking',
    monthlyReimbursements = 'monthlyReimbursements',
    monthlyCarRental = 'monthlyCarRental',
    monthlyParking = 'monthlyParking',
    oneTimeScrubs = 'oneTimeScrubs',
    oneTimeTaxable = 'oneTimeTaxable',
    oneTimeOtherCost = 'oneTimeOtherCost',
    oneTimeOther = 'oneTimeOther',
    legacyPickle = 'legacyPickle',
    monthlyOtherCost = 'monthlyOtherCost',
    monthlyOther = 'monthlyOther',
    bonuses = 'bonuses',
    facilityPaid = 'facilityPaid',
    ayaPaidBonus = 'ayaPaidBonus',
    discretionary = 'discretionary',
    referral = 'referral',
    // reimbursement details
    bonusPickles = 'bonusPickles',
    pickles = 'pickles',
    pickledAmount = 'pickledAmount',
    // housing details
    housingType = 'housingType',
    moveInDate = 'moveInDate',
    moveOutDate = 'moveOutDate',
    continuedHousing = 'continuedHousing',
    familyMembers = 'familyMembers',
    familyMemberInfo = 'familyMemberInfo',
    bedrooms = 'bedrooms',
    pets = 'pets',
    petsInfo = 'petsInfo',
    petType = 'petType',
    petWeight = 'petWeight',
    petAge = 'petAge',
    petBreed = 'petBreed',
    moveOutExpenses = 'moveOutExpenses',
    furniture = 'furniture',
    furnitureDelivery = 'furnitureDelivery',
    utilities = 'utilities',
    // insurance details
    continuedInsurance = 'continuedInsurance',
    medicalDays = 'medicalDays',
    dentalDays = 'dentalDays',
    medicalEffectiveDate = 'medicalEffectiveDate',
    dentalEffectiveDate = 'dentalEffectiveDate',
    // insurance
    providerId = 'providerId',
    needInsurance = 'needInsurance',
    // contract shift
    vacation = 'vacation',
    startDate = 'startDate',
    endDate = 'endDate'
}

export interface MarginForm {
    name: string;
    form: UntypedFormGroup;
}

export interface MarginFormReveal {
    name: string;
    reveal: boolean;
}

@Injectable({
    providedIn: 'root'
})
export class MarginCalculatorFormService {
    private readonly _formInitialized = new Subject<MarginForm>();
    private readonly _revealForm = new BehaviorSubject<MarginFormReveal>(null);
    private readonly _parentForm: UntypedFormGroup;
    private readonly _disabled = new Subject<boolean>();
    private readonly _marginVersion = 7.3;

    private readonly _matRadioButton = 'mat-mdc-radio-button';
    private readonly _matRadioGroup = 'mat-mdc-radio-group';
    private readonly _matCheckbox = 'mat-mdc-checkbox';
    private readonly _matSelect = 'mat-select';
    private readonly _matDatePickerToggle = 'mat-datepicker-toggle';
    private readonly _matIconButton = '.mat-mdc-icon-button';
    private readonly _otBillingRulesElement = '.ot-billing-rules';
    private readonly _matFormField = 'mat-form-field';
    private readonly _matInput = 'input';
    private readonly _matDatePickerToggleButton = 'mat-datepicker-toggle button';
    private readonly _matDatePickerInput = 'input[matInput][matDatepicker]';

    private readonly _matFormFieldDisabled = 'mat-form-field-disabled';
    private readonly _matRadioDisabled = 'mat-mdc-radio-disabled';
    private readonly _matCheckboxDisabled = 'mat-mdc-checkbox-disabled';
    private readonly _matSelectDisabled = 'mat-mdc-select-disabled';
    private readonly _matDatePickerToggleDisabled = 'mat-datepicker-toggle-disabled';

    constructor(private readonly _formBuilder: UntypedFormBuilder) {
        this._parentForm = this._formBuilder.group({});
    }

    get BillingDetailsForm(): AbstractControl {
        return this._parentForm.get(FORM_NAMES.facility_billing_details);
    }

    set formInitialized(form: MarginForm) {
        this._formInitialized.next(form);
    }

    set addControlToParent(form: MarginForm) {
        this._parentForm.setControl(form.name, form.form);
    }

    set revealForm(details: MarginFormReveal) {
        this._revealForm.next(details);
    }

    get revealForm$(): Observable<MarginFormReveal> {
        return this._revealForm.asObservable();
    }

    get formInitialized$(): Observable<MarginForm> {
        return this._formInitialized.asObservable();
    }

    get parentForm(): UntypedFormGroup {
        return this._parentForm;
    }

    get isParentFormValid(): boolean {
        return this._parentForm.valid;
    }

    get parentFormValues(): any {
        return this._parentForm.value;
    }

    get parentFormControls(): { [key: string]: AbstractControl } {
        return this._parentForm.controls;
    }

    get disabled$(): Observable<boolean> {
        return this._disabled.asObservable();
    }

    set disabled(disabled: boolean) {
        this._disabled.next(disabled);
    }

    validateForm(form: string): boolean {
        const formGroup = this._parentForm.get(form);
        if (!formGroup) {
            return false;
        }
        formGroup.markAllAsTouched();
        return formGroup.valid;
    }

    getFormControlValue(form: string, control: string): any {
        return this._parentForm && this._parentForm.get(form) ? this._parentForm.get(form).get(control).value : 0;
    }

    getChildFormControlValue(form: string, childForm: string, control: string): any {
        return this._parentForm && this._parentForm.get(form) && this._parentForm.get(form).get(childForm)
            ? this._parentForm.get(form).get(childForm).get(control).value
            : 0;
    }

    addFormControlValue(form: string, control: AbstractControl) {
        this._parentForm.addControl(form, control);
    }

    convertCtrlNameToName(ctrlName: string): string {
        return this._convertCtrlNameToWords(ctrlName).name;
    }

    getFormControlsByFormName(formName: string): { [key: string]: AbstractControl } {
        if (this._parentForm && this._parentForm.get(formName)) {
            return this._parentForm.get(formName)['controls'];
        } else {
            return null;
        }
    }

    getChildFormValuesFromParent(formName: string): any {
        return this._parentForm.get(formName).value;
    }

    ctrlDefaultChanged(formName: string, ctrlName: string): boolean {
        const ctrl = this._parentForm.get(formName).get(ctrlName);

        return ctrl.invalid || (ctrl.errors && ctrl.errors.defaultChanged);
    }

    listenToChildForm(formName: string, childForm?: string): Observable<any> {
        if (childForm && this._parentForm.get(formName) && this._parentForm.get(formName).get(childForm)) {
            return this._parentForm.get(formName).get(childForm).valueChanges;
        } else if (this._parentForm.get(formName)) {
            return this._parentForm.get(formName).valueChanges;
        } else {
            return of(null);
        }
    }

    listenToForm(formName?: string): Observable<any> {
        if (formName && this._parentForm.get(formName)) {
            return this._parentForm.get(formName).valueChanges;
        } else {
            return this._parentForm.valueChanges;
        }
    }

    listenToFormControl(formName: string, ctrlName: string): Observable<any> {
        if (this._parentForm && this._parentForm.get(formName)) {
            return this._parentForm.get(formName).get(ctrlName).valueChanges;
        } else {
            return of(null);
        }
    }

    getOnCallBillRateControl(): AbstractControl {
        return this.BillingDetailsForm?.get(FORM_CONTROL_NAMES.onCallBillRate);
    }

    getOnCallPayRateControl(): AbstractControl {
        return this.BillingDetailsForm?.get(FORM_CONTROL_NAMES.onCallPayRate);
    }

    getChargeBillRateControl(): AbstractControl {
        return this.BillingDetailsForm?.get(FORM_CONTROL_NAMES.chargeBillRate);
    }

    getChargePayRateControl(): AbstractControl {
        return this.BillingDetailsForm?.get(FORM_CONTROL_NAMES.chargePayRate);
    }

    getCallbackBillingRateControl(): AbstractControl {
        return this.BillingDetailsForm?.get(FORM_CONTROL_NAMES.callbackBillRate);
    }

    getHolidayBillingRateControl(): AbstractControl {
        return this.BillingDetailsForm?.get(FORM_CONTROL_NAMES.holidayBillRate);
    }

    getBillOvertimeControl(): AbstractControl {
        return this.BillingDetailsForm?.get(FORM_CONTROL_NAMES.billOvertime);
    }

    getMarginVersion() {
        return this._marginVersion;
    }

    setFormControlValue(form: string, control: string, value: any): any {
        if (this._parentForm && this._parentForm.get(form) && this._parentForm.get(form).get(control)) {
            this._parentForm.get(form).get(control).patchValue(value, { emitEvent: false });
        }
    }

    setFormReadonly(): void {
        $('mat-form-field:not(.do-not-disable)').addClass(this._matFormFieldDisabled);
        $(this._matRadioButton).addClass(this._matRadioDisabled);
        $(this._matRadioGroup).attr('tabindex', '-1');
        $(this._matRadioGroup).css('pointer-events', 'none');
        $(this._matCheckbox).addClass(this._matCheckboxDisabled);
        $(this._matCheckbox).attr('tabindex', '-1');
        $(this._matSelect).addClass(this._matSelectDisabled);
        $(this._matSelect).attr('tabindex', '-1');

        // Comprehensive datepicker disabling
        $(this._matDatePickerToggle).attr('tabindex', '-1');
        $(this._matDatePickerToggle).attr('disabled', 'true');
        $(this._matDatePickerToggle).addClass(this._matDatePickerToggleDisabled);

        // Disable clicking with CSS pointer-events
        $(this._matDatePickerToggle).css('pointer-events', 'none');
        $(this._matDatePickerToggleButton).css('pointer-events', 'none');
        $('mat-datepicker-toggle .mat-icon-button').css('pointer-events', 'none');

        // Disable all datepicker related elements
        $(this._matDatePickerToggleButton).attr('disabled', 'true');
        $('mat-datepicker-toggle .mat-icon-button').attr('disabled', 'true');
        $(this._matDatePickerInput).attr('disabled', 'true');
        $(this._matDatePickerInput).attr('readonly', 'true');
        $(this._matDatePickerInput).css('pointer-events', 'none');

        // Add visual disabled styling
        $(this._matDatePickerToggle).addClass('disabled-datepicker');
        $(this._matDatePickerInput).addClass('disabled-datepicker');

        $(this._matIconButton).attr('tabindex', '-1');
        $(this._matInput).attr('disabled', 'true');
        $('mat-form-field.do-not-disable input').removeAttr('disabled');

        // Add custom CSS for disabled datepicker if not already added
        if (!$('#disabled-datepicker-styles').length) {
            $('<style id="disabled-datepicker-styles">')
                .text(
                    '.disabled-datepicker { opacity: 0.6 !important; cursor: not-allowed !important; } .disabled-datepicker * { pointer-events: none !important; }'
                )
                .appendTo('head');
        }
    }

    setFormEditable(): void {
        $(this._otBillingRulesElement).off('DOMSubtreeModified');
        $(this._matFormField).removeClass(this._matFormFieldDisabled);
        $(this._matRadioButton).removeClass(this._matRadioDisabled);
        $(this._matRadioGroup).attr('tabindex', '0');
        $(this._matRadioGroup).css('pointer-events', 'auto');
        $(this._matCheckbox).removeClass(this._matCheckboxDisabled);
        $(this._matCheckbox).attr('tabindex', '0');
        $(this._matSelect).removeClass(this._matSelectDisabled);
        $(this._matSelect).attr('tabindex', '0');

        // Re-enable datepicker components
        $(this._matDatePickerToggle).attr('tabindex', '0');
        $(this._matDatePickerToggle).removeAttr('disabled');
        $(this._matDatePickerToggle).removeClass(this._matDatePickerToggleDisabled);

        // Re-enable clicking with CSS pointer-events
        $(this._matDatePickerToggle).css('pointer-events', 'auto');
        $(this._matDatePickerToggle).css('pointer-events', 'auto');
        $(this._matDatePickerToggleButton).css('pointer-events', 'auto');
        $('mat-datepicker-toggle .mat-icon-button').css('pointer-events', 'auto');

        // Re-enable all datepicker related elements
        $(this._matDatePickerToggleButton).removeAttr('disabled');
        $('mat-datepicker-toggle .mat-icon-button').removeAttr('disabled');
        $(this._matDatePickerInput).removeAttr('disabled');
        $(this._matDatePickerInput).removeAttr('readonly');
        $(this._matDatePickerInput).css('pointer-events', 'auto');

        // Remove visual disabled styling
        $(this._matDatePickerToggle).removeClass('disabled-datepicker');
        $(this._matDatePickerInput).removeClass('disabled-datepicker');

        $(this._matIconButton).attr('tabindex', '0');
        $(this._matInput).removeAttr('disabled');
    }

    setControlReadonly(formName: string, controlName: string): void {
        const control = this._parentForm?.get(formName)?.get(controlName);
        if (control) {
            // Disable the Angular form control
            control.disable();

            // Find and disable the corresponding DOM elements
            const inputSelector = `input[formControlName="${controlName}"], input[ng-reflect-name="${controlName}"]`;
            const selectSelector = `mat-select[formControlName="${controlName}"], mat-select[ng-reflect-name="${controlName}"]`;
            const checkboxSelector = `mat-checkbox[formControlName="${controlName}"], mat-checkbox[ng-reflect-name="${controlName}"]`;
            const radioGroupSelector = `mat-radio-group[formControlName="${controlName}"], mat-radio-group[ng-reflect-name="${controlName}"]`;
            const datepickerSelector = `input[matDatepicker][formControlName="${controlName}"], input[matDatepicker][ng-reflect-name="${controlName}"]`;

            // Disable input fields
            $(inputSelector).attr('disabled', 'true');
            $(inputSelector).attr('readonly', 'true');
            $(inputSelector).css('pointer-events', 'none');

            // Disable mat-select
            $(selectSelector).addClass(this._matSelectDisabled);
            $(selectSelector).attr('tabindex', '-1');
            $(selectSelector).css('pointer-events', 'none');

            // Disable mat-checkbox
            $(checkboxSelector).addClass(this._matCheckboxDisabled);
            $(checkboxSelector).attr('tabindex', '-1');
            $(checkboxSelector).css('pointer-events', 'none');

            // Disable mat-radio-group
            $(radioGroupSelector).addClass(this._matRadioDisabled);
            $(radioGroupSelector).attr('tabindex', '-1');
            $(radioGroupSelector).css('pointer-events', 'none');

            // Disable datepicker
            $(datepickerSelector).attr('disabled', 'true');
            $(datepickerSelector).attr('readonly', 'true');
            $(datepickerSelector).css('pointer-events', 'none');
            $(`mat-datepicker-toggle[ng-reflect-for*="${controlName}"]`).addClass(this._matDatePickerToggleDisabled);
            $(`mat-datepicker-toggle[ng-reflect-for*="${controlName}"]`).css('pointer-events', 'none');

            // Disable the parent mat-form-field
            $(inputSelector).closest(this._matFormField).addClass(this._matFormFieldDisabled);
            $(selectSelector).closest(this._matFormField).addClass(this._matFormFieldDisabled);
            $(checkboxSelector).closest(this._matFormField).addClass(this._matFormFieldDisabled);
            $(radioGroupSelector).closest(this._matFormField).addClass(this._matFormFieldDisabled);
            $(datepickerSelector).closest(this._matFormField).addClass(this._matFormFieldDisabled);
        }
    }

    setControlEditable(formName: string, controlName: string): void {
        const control = this._parentForm?.get(formName)?.get(controlName);
        if (control) {
            // Enable the Angular form control
            control.enable();

            // Find and enable the corresponding DOM elements
            const inputSelector = `input[formControlName="${controlName}"], input[ng-reflect-name="${controlName}"]`;
            const selectSelector = `mat-select[formControlName="${controlName}"], mat-select[ng-reflect-name="${controlName}"]`;
            const checkboxSelector = `mat-checkbox[formControlName="${controlName}"], mat-checkbox[ng-reflect-name="${controlName}"]`;
            const radioGroupSelector = `mat-radio-group[formControlName="${controlName}"], mat-radio-group[ng-reflect-name="${controlName}"]`;
            const datepickerSelector = `input[matDatepicker][formControlName="${controlName}"], input[matDatepicker][ng-reflect-name="${controlName}"]`;

            // Enable input fields
            $(inputSelector).removeAttr('disabled');
            $(inputSelector).removeAttr('readonly');
            $(inputSelector).css('pointer-events', 'auto');

            // Enable mat-select
            $(selectSelector).removeClass(this._matSelectDisabled);
            $(selectSelector).attr('tabindex', '0');
            $(selectSelector).css('pointer-events', 'auto');

            // Enable mat-checkbox
            $(checkboxSelector).removeClass(this._matCheckboxDisabled);
            $(checkboxSelector).attr('tabindex', '0');
            $(checkboxSelector).css('pointer-events', 'auto');

            // Enable mat-radio-group
            $(radioGroupSelector).removeClass(this._matRadioDisabled);
            $(radioGroupSelector).attr('tabindex', '0');
            $(radioGroupSelector).css('pointer-events', 'auto');

            // Enable datepicker
            $(datepickerSelector).removeAttr('disabled');
            $(datepickerSelector).removeAttr('readonly');
            $(datepickerSelector).css('pointer-events', 'auto');
            $(`mat-datepicker-toggle[ng-reflect-for*="${controlName}"]`).removeClass(this._matDatePickerToggleDisabled);
            $(`mat-datepicker-toggle[ng-reflect-for*="${controlName}"]`).css('pointer-events', 'auto');

            // Enable the parent mat-form-field
            $(inputSelector).closest(this._matFormField).removeClass(this._matFormFieldDisabled);
            $(selectSelector).closest(this._matFormField).removeClass(this._matFormFieldDisabled);
            $(checkboxSelector).closest(this._matFormField).removeClass(this._matFormFieldDisabled);
            $(radioGroupSelector).closest(this._matFormField).removeClass(this._matFormFieldDisabled);
            $(datepickerSelector).closest(this._matFormField).removeClass(this._matFormFieldDisabled);
        }
    }

    private _convertCtrlNameToWords(value: string, type?: string): { name: string; type: string } {
        const reimbursementName = value.replace(/([A-Z])/g, ' $1').replace(/^./, (str) => str.toUpperCase());
        let reimbursementType = '';

        if (type) {
            reimbursementType = reimbursementName.substr(type.length + 1, reimbursementName.length);
        }

        return {
            name: reimbursementName,
            type: reimbursementType
        };
    }
}
